import React from 'react';
import {
  IconCalculator,
  IconChart,
  IconDashboard,
  IconMessageSquare,
  IconShield,
  IconTrendUp,
  IconUsers,
  IconZap,
  IconScale,
  IconAlert,
} from '../components/Icons';

/**
 * What RiskLo offers, in one place: the Products and Solutions menus, the
 * homepage grids and the Features page all read this. A product's `anchor` is
 * its section id on the Features page.
 *
 * tag: 'New' | 'Pro' | 'Free' | 'Companies'
 */
export const PRODUCTS = [
  {
    id: 'ai',
    title: 'RiskLo AI',
    tag: 'New',
    icon: <IconMessageSquare size={18} />,
    tone: 'violet',
    short: 'Ask about your own accounts, the market calendar and prop firm rules.',
    text: 'An assistant that knows RiskLo and your data. Ask how your accounts did this week, what’s on the economic calendar, what a prop firm’s rules say, or why a number is what it is. It explains facts and never tells you what to trade.',
    bullets: [
      'Answers from your own connected accounts, trades and results',
      'Economic calendar, Fed meetings, earnings dates and market news',
      'Prop firm rules, verified against each firm’s own pages and linked',
      'Calculators: position size, points to dollars, Monte Carlo, prop firm replay',
      'Deep mode for longer analysis',
    ],
  },
  {
    id: 'portfolio',
    title: 'My Portfolio',
    tag: 'Pro',
    icon: <IconTrendUp size={18} />,
    tone: 'green',
    short: 'Every broker account in one place: balances, equity, trades, positions.',
    text: 'Connect up to 5 accounts read-only and see them together: net liquidity, equity curve, returns, open positions and every trade.',
    bullets: [
      'NinjaTrader / Tradovate (live and demo), Coinbase, Alpaca, Interactive Brokers',
      'Schwab, Robinhood, Webull, Fidelity, E*TRADE, Vanguard and more through SnapTrade',
      'Read-only: RiskLo never places trades',
      'Returns, drawdown and win days for each account and all of them together',
    ],
  },
  {
    id: 'prop-risk',
    title: 'Prop firm risk tools',
    tag: 'Pro',
    icon: <IconCalculator size={18} />,
    tone: 'blue',
    short: 'Blowout Risk, % Rule, Hedging and Bulk Risk with prop firm presets.',
    text: 'Know before you trade whether a strategy can survive its worst historical day at your size, and whether it keeps you inside your firm’s consistency rule.',
    bullets: [
      'Account Blowout Risk: a clear GO / NO GO with end-of-day blowout probability',
      '% Rule (consistency) Risk with presets for major prop firms',
      'Hedging Risk across a pair of futures strategies',
      'Bulk Risk: up to 40 account and strategy setups at once, or drop in a NinjaTrader CSV',
    ],
  },
  {
    id: 'portfolio-risk',
    title: 'Portfolio risk tools',
    tag: 'Pro',
    icon: <IconScale size={18} />,
    tone: 'amber',
    short: 'Position sizing, concentration, drawdown & VaR, stops and rebalancing.',
    text: 'For stocks, ETFs and crypto in your brokerage accounts: size positions to a risk budget, see where you’re concentrated, and plan stops and rebalancing from your real holdings.',
    bullets: [
      'Position Sizer: shares from your risk per trade and stop',
      'Concentration Risk: each holding’s share, top 5, and a stress test',
      'Drawdown & Value at Risk from your account history (deposits don’t count as gains)',
      'Stop Planner and Rebalancing to target weights (RiskLo never places orders)',
    ],
  },
  {
    id: 'simulator',
    title: 'Portfolio Simulator',
    tag: 'Pro',
    icon: <IconDashboard size={18} />,
    tone: 'violet',
    short: 'Combine strategies and accounts and see the result before you go live.',
    text: 'Rank strategies by profit factor, win rate or risk for each weekday, then tick any mix of strategies and your own accounts and simulate them together: combined daily P&L, drawdown, projections and risk checks.',
    bullets: [
      'Rank by weekday and overall over any date range',
      'Mix company strategies, your accounts and your own strategies',
      'NQ / MNQ toggle and contract multiplier',
      'Blowout and % Rule checks on the combined result',
    ],
  },
  {
    id: 'results',
    title: 'Strategy results',
    tag: 'Free',
    icon: <IconChart size={18} />,
    tone: 'blue',
    short: 'Your company’s strategy history: calendar, chart, weekly and spreadsheet.',
    text: 'See the strategies your company publishes, with their full history in a calendar, chart, weekly table and spreadsheet. Pro adds the detailed KPI metrics.',
    bullets: [
      'Calendar, chart, weekly and spreadsheet views',
      'Date ranges, grouping and search',
      'Roll-week and high-impact event markers',
      'NQ ↔ MNQ rescaling on the fly',
    ],
  },
  {
    id: 'dashboards',
    title: 'Custom dashboards',
    tag: 'Pro',
    icon: <IconZap size={18} />,
    tone: 'green',
    short: 'Build up to 5 dashboards from your own accounts and widgets.',
    text: 'Drag, resize and rename widgets built from your own accounts: KPIs, calendars, equity curves and market widgets. Start from a template and set a default.',
    bullets: [
      'Up to 5 dashboards, one click apart',
      'Templates like the Master dashboard',
      'TradingView market widgets',
    ],
  },
  {
    id: 'trade-watcher',
    title: 'Trade Watcher',
    tag: 'Companies',
    icon: <IconAlert size={18} />,
    tone: 'amber',
    short: 'Live NinjaTrader health and naked-position alerts for your clients.',
    text: 'The RiskLo Tally add-on reports each NinjaTrader account live: connection, P&L and any open position without a protective stop. Alerts go out in seconds, and the self-use build can close a naked position automatically.',
    bullets: [
      'Naked-position alerts with an optional auto-close',
      'Connection and add-on health for every account',
      'Alert-only build for managed (CSA) accounts',
    ],
  },
];

export const SOLUTIONS = [
  {
    id: 'prop',
    title: 'For prop firm traders',
    tone: 'blue',
    icon: <IconShield size={18} />,
    short: 'Pass evaluations and keep payouts: check every setup against your firm’s rules.',
    text: 'Drawdown rules, consistency limits and contract scaling are where funded accounts are lost. RiskLo checks each setup against your firm’s rules before you trade, and RiskLo AI explains the rules in plain English.',
    products: ['prop-risk', 'ai', 'simulator'],
  },
  {
    id: 'futures',
    title: 'For futures & NinjaTrader traders',
    tone: 'violet',
    icon: <IconTrendUp size={18} />,
    short: 'Live and demo accounts together, NQ / MNQ scaling and CSV in one step.',
    text: 'Connect your NinjaTrader or Tradovate accounts, see every account’s equity and trades in one place, and run a NinjaTrader export straight through Bulk Risk.',
    products: ['portfolio', 'prop-risk', 'dashboards'],
  },
  {
    id: 'investors',
    title: 'For stock & crypto investors',
    tone: 'green',
    icon: <IconScale size={18} />,
    short: 'Size positions, spot concentration and track every brokerage account.',
    text: 'Schwab, Robinhood, Webull, Fidelity, Coinbase and more in one view, with tools to size positions, see concentration, and plan stops and rebalancing.',
    products: ['portfolio', 'portfolio-risk', 'ai'],
  },
  {
    id: 'companies',
    title: 'For strategy providers & communities',
    tone: 'amber',
    icon: <IconUsers size={18} />,
    short: 'Your own branded RiskLo for your members, strategies and clients.',
    text: 'Publish your strategies, give members their own risk tools under your brand, and watch your clients’ NinjaTrader accounts live.',
    products: ['results', 'trade-watcher', 'simulator'],
    page: 'companies',
  },
];

export const productById = (id) => PRODUCTS.find((p) => p.id === id);

/** Section id for a product or solution on the Features page. */
export const anchorFor = (kind, id) => `${kind}-${id}`;
